import { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  ValidateSecurityInputSchema,
  type Config,
  type SecurityValidationResult,
  type SecurityFinding,
  type SecurityCheck,
} from '../types/index.js';
import { findFiles, readText, validatePathSecurity } from '../utils/fs.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import path from 'path';

export const validateSecurityTool: Tool = {
  name: 'validate_security',
  description: 'Validate SmartStack security patterns: multi-tenant isolation, authorization, input validation, secrets exposure, injection vulnerabilities, and OWASP Top 10 compliance.',
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Project path to validate (default: SmartStack.app path from config)',
      },
      checks: {
        type: 'array',
        items: {
          type: 'string',
          enum: [
            'hardcoded-secrets',
            'sql-injection',
            'tenant-isolation',
            'authorization',
            'dangerous-functions',
            'input-validation',
            'xss',
            'csrf',
            'logging-sensitive',
            'all',
          ],
        },
        description: 'Security checks to run',
        default: ['all'],
      },
      severity: {
        type: 'string',
        enum: ['blocking', 'all'],
        description: 'Filter results by severity',
      },
    },
  },
};

export async function handleValidateSecurity(
  args: unknown,
  config: Config
): Promise<string> {
  const input = ValidateSecurityInputSchema.parse(args);
  const projectPath = input.path || config.smartstack.projectPath;
  const checksToRun = input.checks.includes('all')
    ? [
        'hardcoded-secrets',
        'sql-injection',
        'tenant-isolation',
        'authorization',
        'dangerous-functions',
        'input-validation',
      ] as SecurityCheck[]
    : input.checks;

  logger.info('Validating security', { projectPath, checks: checksToRun });

  const result: SecurityValidationResult = {
    valid: true,
    summary: '',
    findings: [],
    stats: {
      blocking: 0,
      critical: 0,
      warning: 0,
      filesScanned: 0,
    },
  };

  const structure = await findSmartStackStructure(projectPath);

  // Run selected checks
  if (checksToRun.includes('hardcoded-secrets')) {
    await checkHardcodedSecrets(structure, result);
  }

  if (checksToRun.includes('sql-injection')) {
    await checkSqlInjection(structure, result);
  }

  if (checksToRun.includes('tenant-isolation')) {
    await checkTenantIsolation(structure, result);
  }

  if (checksToRun.includes('authorization')) {
    await checkAuthorization(structure, result);
  }

  if (checksToRun.includes('dangerous-functions')) {
    await checkDangerousFunctions(structure, result);
  }

  if (checksToRun.includes('input-validation')) {
    await checkInputValidation(structure, result);
  }

  // Calculate stats
  result.stats.blocking = result.findings.filter(f => f.severity === 'blocking').length;
  result.stats.critical = result.findings.filter(f => f.severity === 'critical').length;
  result.stats.warning = result.findings.filter(f => f.severity === 'warning').length;

  // Filter by severity if requested
  if (input.severity === 'blocking') {
    result.findings = result.findings.filter(f => f.severity === 'blocking');
  }

  // Update validity
  result.valid = result.stats.blocking === 0;

  // Generate summary
  result.summary = result.valid
    ? `Security validation passed. ${result.stats.warning} warning(s) found.`
    : `Security validation FAILED. ${result.stats.blocking} blocking issue(s) found.`;

  return formatSecurityReport(result);
}

// ============================================================================
// Security Check Functions
// ============================================================================

async function checkHardcodedSecrets(
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>,
  result: SecurityValidationResult
): Promise<void> {
  const secretPatterns = [
    // Credentials
    { pattern: /(?:password|passwd|pwd)\s*[=:]\s*["'][^"']{4,}["']/gi, name: 'password' },
    { pattern: /(?:api[_-]?key|apikey)\s*[=:]\s*["'][^"']{8,}["']/gi, name: 'API key' },
    { pattern: /(?:secret|token)\s*[=:]\s*["'][^"']{8,}["']/gi, name: 'secret/token' },
    // Connection strings with embedded passwords
    { pattern: /Server=.+;.*Password=[^;]+/gi, name: 'connection string with password' },
    { pattern: /Data Source=.+;.*Password=[^;]+/gi, name: 'connection string with password' },
    // Private keys
    { pattern: /-----BEGIN (?:RSA |EC |DSA )?PRIVATE KEY-----/g, name: 'private key' },
  ];

  const filesToScan = await getFilesToScan(structure, ['**/*.cs', '**/*.ts', '**/*.tsx', '**/*.json']);
  result.stats.filesScanned += filesToScan.length;

  for (const file of filesToScan) {
    // Skip excluded files
    if (isExcludedFile(file)) continue;

    const content = await readText(file);
    const lines = content.split('\n');

    for (const { pattern, name } of secretPatterns) {
      pattern.lastIndex = 0; // Reset regex state
      let match;
      while ((match = pattern.exec(content)) !== null) {
        const lineNumber = getLineNumber(content, match.index);
        const lineContent = lines[lineNumber - 1]?.trim() || '';

        // Skip if it's a configuration placeholder or example
        if (isPlaceholderValue(lineContent)) continue;

        result.findings.push({
          severity: 'blocking',
          category: 'hardcoded-secrets',
          message: `Hardcoded ${name} detected`,
          file: path.relative(structure.root, file),
          line: lineNumber,
          code: truncateCode(lineContent),
          suggestion: `Move ${name} to configuration or environment variables`,
          cweId: 'CWE-798',
        });
      }
    }
  }
}

async function checkSqlInjection(
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>,
  result: SecurityValidationResult
): Promise<void> {
  const sqlInjectionPatterns = [
    // Raw SQL with string concatenation (the most dangerous pattern)
    { pattern: /\.(?:FromSqlRaw|ExecuteSqlRaw)\s*\([^)]*\s*\+\s*/g, name: 'concatenated SQL in FromSqlRaw/ExecuteSqlRaw' },
    // ADO.NET without parameters
    { pattern: /new SqlCommand\s*\([^)]*\+/g, name: 'concatenated SQL in SqlCommand' },
    { pattern: /CommandText\s*=\s*[^;]*\+/g, name: 'concatenated SQL in CommandText' },
    // Note: FromSqlInterpolated with $"" is safe and not flagged
  ];

  const csFiles = await getFilesToScan(structure, ['**/*.cs']);
  result.stats.filesScanned += csFiles.length;

  for (const file of csFiles) {
    if (isExcludedFile(file)) continue;

    const content = await readText(file);
    const lines = content.split('\n');

    for (const { pattern, name } of sqlInjectionPatterns) {
      pattern.lastIndex = 0;
      let match;
      while ((match = pattern.exec(content)) !== null) {
        const lineNumber = getLineNumber(content, match.index);
        const lineContent = lines[lineNumber - 1]?.trim() || '';

        result.findings.push({
          severity: 'blocking',
          category: 'sql-injection',
          message: `Potential SQL injection: ${name}`,
          file: path.relative(structure.root, file),
          line: lineNumber,
          code: truncateCode(lineContent),
          suggestion: 'Use parameterized queries: FromSqlRaw("SELECT * FROM x WHERE id = {0}", id) or FromSqlInterpolated',
          cweId: 'CWE-89',
        });
      }
    }
  }
}

async function checkTenantIsolation(
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>,
  result: SecurityValidationResult
): Promise<void> {
  if (!structure.domain) {
    result.findings.push({
      severity: 'warning',
      category: 'tenant-isolation',
      message: 'Domain project not found, skipping tenant isolation validation',
      file: '',
      suggestion: 'Ensure project structure follows SmartStack conventions',
    });
    return;
  }

  // Find entities implementing ITenantEntity
  const entityFiles = await findFiles('**/Entities/**/*.cs', { cwd: structure.domain });
  const tenantEntities: string[] = [];

  for (const file of entityFiles) {
    const content = await readText(file);
    if (content.includes('ITenantEntity') || content.includes(': TenantEntity')) {
      const entityMatch = content.match(/class\s+(\w+)/);
      if (entityMatch) {
        tenantEntities.push(entityMatch[1]);
      }
    }

    // Check for Create methods missing TenantId parameter
    const createMethodMatches = content.matchAll(/public\s+static\s+\w+\s+Create\s*\(([^)]*)\)/g);
    for (const match of createMethodMatches) {
      const params = match[1];
      if ((content.includes('ITenantEntity') || content.includes(': TenantEntity')) && !params.includes('tenantId') && !params.includes('TenantId')) {
        const lineNumber = getLineNumber(content, match.index!);
        result.findings.push({
          severity: 'blocking',
          category: 'tenant-isolation',
          message: 'Create method missing tenantId parameter for tenant entity',
          file: path.relative(structure.root, file),
          line: lineNumber,
          code: truncateCode(match[0]),
          suggestion: 'Add Guid tenantId as first parameter: Create(Guid tenantId, ...)',
          cweId: 'CWE-639',
        });
      }
    }
  }

  // Check services for direct DbContext access without tenant filtering
  if (structure.application) {
    const serviceFiles = await findFiles('**/*Service.cs', { cwd: structure.application });
    for (const file of serviceFiles) {
      const content = await readText(file);

      // Pattern: Direct access without Where clause
      const directAccessPattern = /_context\.\w+\.(?:First|Single|Find|ToList)\s*\(/g;
      let match;
      while ((match = directAccessPattern.exec(content)) !== null) {
        // Check if there's a Where clause nearby (simple heuristic)
        const surroundingCode = content.substring(Math.max(0, match.index - 100), match.index + 100);
        if (!surroundingCode.includes('.Where(') && !surroundingCode.includes('TenantId')) {
          const lineNumber = getLineNumber(content, match.index);
          result.findings.push({
            severity: 'critical',
            category: 'tenant-isolation',
            message: 'Direct DbContext access without tenant filtering',
            file: path.relative(structure.root, file),
            line: lineNumber,
            code: truncateCode(match[0]),
            suggestion: 'Use repository with global tenant filter or add explicit TenantId filter',
            cweId: 'CWE-639',
          });
        }
      }
    }
  }
}

async function checkAuthorization(
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>,
  result: SecurityValidationResult
): Promise<void> {
  const apiPath = structure.api || structure.apiCore;
  if (!apiPath) {
    result.findings.push({
      severity: 'warning',
      category: 'authorization',
      message: 'API project not found, skipping authorization validation',
      file: '',
      suggestion: 'Ensure project structure follows SmartStack conventions',
    });
    return;
  }

  const controllerFiles = await findFiles('**/Controllers/**/*.cs', { cwd: apiPath });
  result.stats.filesScanned += controllerFiles.length;

  for (const file of controllerFiles) {
    const content = await readText(file);

    // Check for [ApiController] without authorization
    if (content.includes('[ApiController]')) {
      const hasClassLevelAuth =
        content.includes('[Authorize]') ||
        content.includes('[NavRoute(') ||
        content.includes('[AllowAnonymous]');

      if (!hasClassLevelAuth) {
        // Extract controller name
        const controllerMatch = content.match(/class\s+(\w+Controller)/);
        const controllerName = controllerMatch ? controllerMatch[1] : 'Unknown';
        const lineNumber = controllerMatch ? getLineNumber(content, controllerMatch.index!) : 1;

        result.findings.push({
          severity: 'blocking',
          category: 'authorization',
          message: `Controller ${controllerName} missing authorization attribute`,
          file: path.relative(structure.root, file),
          line: lineNumber,
          suggestion: 'Add [NavRoute("context.application.module")] or [Authorize] attribute to the controller class',
          cweId: 'CWE-862',
        });
      }
    }
  }
}

async function checkDangerousFunctions(
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>,
  result: SecurityValidationResult
): Promise<void> {
  const csharpPatterns = [
    { pattern: /Process\.Start\s*\([^)]*(?:user|input|param|request)/gi, name: 'Process.Start with user input' },
    { pattern: /Assembly\.Load(?:From|File)?\s*\(/g, name: 'Dynamic assembly loading' },
    { pattern: /Type\.GetType\s*\([^)]*(?:user|input|param|request)/gi, name: 'Type.GetType with user input' },
    { pattern: /Activator\.CreateInstance\s*\([^)]*(?:user|input|param|request)/gi, name: 'Activator.CreateInstance with user input' },
  ];

  const typescriptPatterns = [
    { pattern: /\beval\s*\(/g, name: 'eval() usage' },
    { pattern: /new\s+Function\s*\(/g, name: 'new Function() usage' },
    { pattern: /child_process\.exec\s*\(/g, name: 'child_process.exec usage' },
    { pattern: /\.innerHTML\s*=/g, name: 'innerHTML assignment' },
    { pattern: /dangerouslySetInnerHTML/g, name: 'dangerouslySetInnerHTML usage' },
  ];

  // Check C# files
  const csFiles = await getFilesToScan(structure, ['**/*.cs']);
  for (const file of csFiles) {
    if (isExcludedFile(file)) continue;
    const content = await readText(file);
    await checkPatterns(file, content, csharpPatterns, structure, result);
  }

  // Check TypeScript files
  const tsFiles = await getFilesToScan(structure, ['**/*.ts', '**/*.tsx']);
  for (const file of tsFiles) {
    if (isExcludedFile(file)) continue;
    const content = await readText(file);
    await checkPatterns(file, content, typescriptPatterns, structure, result);
  }
}

async function checkPatterns(
  file: string,
  content: string,
  patterns: Array<{ pattern: RegExp; name: string }>,
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>,
  result: SecurityValidationResult
): Promise<void> {
  const lines = content.split('\n');

  for (const { pattern, name } of patterns) {
    pattern.lastIndex = 0;
    let match;
    while ((match = pattern.exec(content)) !== null) {
      const lineNumber = getLineNumber(content, match.index);
      const lineContent = lines[lineNumber - 1]?.trim() || '';

      result.findings.push({
        severity: 'critical',
        category: 'dangerous-functions',
        message: `Dangerous function: ${name}`,
        file: path.relative(structure.root, file),
        line: lineNumber,
        code: truncateCode(lineContent),
        suggestion: 'Avoid using dangerous functions with user-controlled input. Use safe alternatives.',
        cweId: 'CWE-78',
      });
    }
  }
}

async function checkInputValidation(
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>,
  result: SecurityValidationResult
): Promise<void> {
  if (!structure.application) {
    return;
  }

  // Find DTOs
  const dtoFiles = await findFiles('**/*Dto.cs', { cwd: structure.application });
  const dtos: string[] = [];

  for (const file of dtoFiles) {
    const content = await readText(file);
    const dtoMatch = content.match(/class\s+(\w+Dto)/);
    if (dtoMatch) {
      dtos.push(dtoMatch[1]);
    }
  }

  // Find validators
  const validatorFiles = await findFiles('**/*Validator.cs', { cwd: structure.application });
  const validators: string[] = [];

  for (const file of validatorFiles) {
    const content = await readText(file);
    const validatorMatch = content.match(/class\s+(\w+Validator)/);
    if (validatorMatch) {
      validators.push(validatorMatch[1]);
    }
  }

  // Check for DTOs without validators
  for (const dto of dtos) {
    const expectedValidator = dto.replace(/Dto$/, 'DtoValidator');
    const hasValidator = validators.some(v => v === expectedValidator || v === `${dto}Validator`);

    if (!hasValidator) {
      result.findings.push({
        severity: 'warning',
        category: 'input-validation',
        message: `DTO ${dto} has no FluentValidation validator`,
        file: `Application/DTOs/${dto}.cs`,
        suggestion: `Create ${expectedValidator} class with FluentValidation rules`,
        cweId: 'CWE-20',
      });
    }
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

async function getFilesToScan(
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>,
  patterns: string[]
): Promise<string[]> {
  const allFiles: string[] = [];

  for (const pattern of patterns) {
    const files = await findFiles(pattern, { cwd: structure.root });
    // Validate each file path stays within project root
    for (const file of files) {
      try {
        validatePathSecurity(file, structure.root);
        allFiles.push(file);
      } catch {
        // Skip files that fail path validation (potential traversal)
        logger.warn('Skipping file outside project root', { file });
      }
    }
  }

  return [...new Set(allFiles)]; // Remove duplicates
}

function isExcludedFile(filePath: string): boolean {
  const exclusions = [
    /[/\\]bin[/\\]/,
    /[/\\]obj[/\\]/,
    /[/\\]node_modules[/\\]/,
    /\.example\./,
    /\.template\./,
    /\.test\./,
    /\.spec\./,
    /Tests[/\\]/,
    /appsettings\.Development\.json$/,
    /appsettings\.Template\.json$/,
  ];

  return exclusions.some(pattern => pattern.test(filePath));
}

function isPlaceholderValue(line: string): boolean {
  // Only match explicit environment variable syntax, not general TODO comments
  const placeholderPatterns = [
    /\${[A-Z_][A-Z0-9_]*}/,  // ${VAR_NAME} - env var syntax
    /%[A-Z_][A-Z0-9_]*%/,    // %VAR_NAME% - Windows env var
    /\{\{[A-Za-z_][A-Za-z0-9_]*\}\}/, // {{varName}} - template syntax
    /["']your[_-]?(?:password|secret|key|token)["']/i, // "your_password" placeholders
    /["']changeme["']/i,
    /["']<[A-Z_]+>["']/,  // "<REPLACE_ME>" syntax
    /["']xxx+["']/i,      // "xxx" or "XXXX" placeholders
    /=\s*["']["']/,       // Empty string assignments
  ];

  return placeholderPatterns.some(pattern => pattern.test(line));
}

function getLineNumber(content: string, index: number): number {
  // Handle both LF and CRLF line endings
  const normalizedContent = content.substring(0, index).replace(/\r\n/g, '\n');
  return normalizedContent.split('\n').length;
}

function truncateCode(code: string, maxLength: number = 80): string {
  return code.length > maxLength ? code.substring(0, maxLength) + '...' : code;
}

function formatSecurityReport(result: SecurityValidationResult): string {
  const lines: string[] = [];

  lines.push('# Security Validation Report');
  lines.push('');
  lines.push('## Summary');
  lines.push(`- **Status**: ${result.valid ? '✅ PASSED' : '❌ FAILED'}`);
  lines.push(`- **Blocking**: ${result.stats.blocking}`);
  lines.push(`- **Critical**: ${result.stats.critical}`);
  lines.push(`- **Warnings**: ${result.stats.warning}`);
  lines.push(`- **Files Scanned**: ${result.stats.filesScanned}`);
  lines.push('');

  if (result.findings.length === 0) {
    lines.push('No security issues found.');
    return lines.join('\n');
  }

  // Group by severity
  const blocking = result.findings.filter(f => f.severity === 'blocking');
  const critical = result.findings.filter(f => f.severity === 'critical');
  const warnings = result.findings.filter(f => f.severity === 'warning');

  if (blocking.length > 0) {
    lines.push('## Blocking Issues');
    lines.push('');
    for (const finding of blocking) {
      formatFinding(finding, lines);
    }
  }

  if (critical.length > 0) {
    lines.push('## Critical Issues');
    lines.push('');
    for (const finding of critical) {
      formatFinding(finding, lines);
    }
  }

  if (warnings.length > 0) {
    lines.push('## Warnings');
    lines.push('');
    for (const finding of warnings) {
      formatFinding(finding, lines);
    }
  }

  return lines.join('\n');
}

function formatFinding(finding: SecurityFinding, lines: string[]): void {
  lines.push(`### ${finding.category}: ${finding.message}`);
  if (finding.file) {
    lines.push(`- **File**: \`${finding.file}${finding.line ? `:${finding.line}` : ''}\``);
  }
  if (finding.code) {
    lines.push(`- **Code**: \`${finding.code}\``);
  }
  if (finding.cweId) {
    lines.push(`- **CWE**: ${finding.cweId}`);
  }
  lines.push(`- **Fix**: ${finding.suggestion}`);
  lines.push('');
}
